<template>
  <div class="aei_container">
    <div class="left_section">
      <div class="effect_label">
        <template v-if="automationEffect.label">
          <span class="extra_label" v-text="$t(automationEffect.label)" />
        </template>
        <template v-else>
          <span
            v-if="!automationEffect.effectExtraLabel"
            v-text="$t(automationEffect.effectLabel)"
          />
          <span
            v-else
            class="extra_label"
            v-text="` ${$t(automationEffect.effectExtraLabel)}`"
          />
        </template>
      </div>
    </div>
    <div v-if="recentLogs.length" class="middle_section">
      <div class="log_item custom_tooltip">
        <BaseIcon icon="$mdiCheck" color="white" small />
        <div class="tooltip_content">
          <div v-for="log in recentLogs" :key="log.id">
            <div class="author">
              {{
                log.author
                  ? $t("automation.started_by", { name: log.author })
                  : $t("automation.send_automatically")
              }}
            </div>
            <div class="date_created">
              {{
                `${$t("utils.on")} ${$d(
                  new Date(log.dateCreated),
                  "veryshort"
                )}`
              }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="right_section">
      <div class="action_section">
        <button
          v-if="!doingAction"
          :disabled="!!errorMessage"
          class="action_btn"
          :class="{
            custom_tooltip: !!errorMessage,
          }"
          @click="runEffect"
        >
          <div v-if="errorMessage" class="tooltip_content">
            {{ errorMessage }}
          </div>
          <BaseIcon class="btn_icon" icon="$mdiPlay" />
        </button>
        <v-progress-circular
          v-else
          :size="22"
          class="action_progress"
          indeterminate
          color="primary"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { AutomationEffect } from "@/core/Models/AutomationEffect";
import { AUTOMATION_EFFECT } from "@/core/Utils/types.utils";
import FolderAPI from "@/modules/Folder/Services/folder.api";
export default {
  name: "AutomationEffectItem",
  props: {
    automationEffect: {
      type: AutomationEffect,
      required: true,
    },
    hasEnoxa: {
      type: Boolean,
    },
    statusId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      doingAction: false,
    };
  },
  computed: {
    ...mapState({
      activeFolder: (state) => state.folder.activeFolder,
    }),
    errorMessage() {
      switch (this.automationEffect.action) {
        case AUTOMATION_EFFECT.SEND_FORM:
        case AUTOMATION_EFFECT.SEND_OFFER:
        case AUTOMATION_EFFECT.SEND_CAMPAIGN:
          if (this.activeFolder?.client?.isTemporary)
            return this.$t("utils.require_assign_contact");
          break;
        case AUTOMATION_EFFECT.SEND_TO_GED:
          if (!this.hasEnoxa) return this.$t("utils.enoxa_required");
          break;
      }
      if (
        this.automationEffect.unique &&
        this.automationEffect.logs.length != 0
      )
        return this.$t("automation.dialog.one_time_effect");
      return "";
    },
    recentLogs() {
      return this.automationEffect.logs.slice().reverse();
    },
    activeInDetail() {
      if (this.automationEffect.activateIn == 0)
        return this.$t("activation.today");
      if (!this.automationEffect.nextLaunch?.date) return "";
      if (this.automationEffect.nextLaunch.remaining >= 1) {
        return this.$tc(
          "activation.future",
          this.automationEffect.nextLaunch.remaining,
          {
            date: this.$d(
              new Date(this.automationEffect.nextLaunch.date),
              "veryshort"
            ),
            days: this.automationEffect.nextLaunch.remaining,
          }
        );
      }
      return this.$tc(
        "activation.past",
        Math.abs(this.automationEffect.nextLaunch.remaining),
        {
          date: this.$d(
            new Date(this.automationEffect.nextLaunch.date),
            "veryshort"
          ),
          days: Math.abs(this.automationEffect.nextLaunch.remaining),
        }
      );
    },
  },
  methods: {
    async runEffect() {
      if (this.errorMessage) return;
      this.doingAction = true;
      try {
        await FolderAPI.postFolderEffect({
          organizationId: this.$route.params.organizationId,
          folderId: this.$route.params.folderId,
          effectId: this.automationEffect.id,
        });
        switch (this.automationEffect.action) {
          case AUTOMATION_EFFECT.SEND_FORM:
          case AUTOMATION_EFFECT.SEND_OFFER:
          case AUTOMATION_EFFECT.SEND_CAMPAIGN:
            await this.$store.dispatch(
              "folder/getOrganizationOffersRecordFolder",
              {
                organizationId: this.$route.params.organizationId,
                folderId: this.$route.params.folderId,
                page: 1,
              }
            );
            break;
          case AUTOMATION_EFFECT.ARCHIVE_FOLDER:
          case AUTOMATION_EFFECT.ASSIGN_COLLABORATOR:
          case AUTOMATION_EFFECT.SET_STATUS:
            await this.$store.dispatch("folder/fetchActiveFolder", {
              organizationId: this.$route.params.organizationId,
              folderId: this.$route.params.folderId,
            });
            await this.$store.dispatch("folder/fetchClientOwnfolder", {
              organizationId: this.$route.params.organizationId,
              folderId: this.$route.params.folderId,
            });
            break;
          case AUTOMATION_EFFECT.GENERATE_DOC:
            if (this.activeFolder.workflow) {
              this.$store.commit("workflow/SET_ACTIVE_WORKFLOW", null);
              await this.$store.dispatch("workflow/fetchActiveWorkflow", {
                organizationId: this.$route.params.organizationId,
                workflowId: this.activeFolder.workflow,
              });
            }
            break;
        }
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.action_successfully_completed"),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.$store.dispatch("workflow/fetchFolderStatusEffects", {
        organizationId: this.$route.params.organizationId,
        folderId: this.$route.params.folderId,
        statusId: this.statusId,
      });
      this.doingAction = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.aei_container {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  .left_section {
    flex: none;
    width: calc(100% - 52px);
    .effect_label {
      color: #707080;
      font-family: Inter;
      font-size: 12px;
      line-height: 16px;
      font-style: normal;
      font-weight: 600;
      .extra_label {
        color: #2536cc;
      }
    }
  }
  .middle_section {
    flex: none;
    width: 22px;
    height: 22px;
    .log_item {
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #18a300;
      border-radius: 50%;
    }
    .custom_tooltip {
      position: relative;
      .tooltip_content {
        position: absolute;
        top: 0;
        right: 0;
        transform: translate(110px, calc(-100% - 20px));
        padding: 12px 16px;
        border-radius: 5px;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.25s;
        background-color: #707080;
        z-index: 10;
        font-size: 14px;
        line-height: 18px;
        color: #fff;
        text-align: start;
        display: flex;
        flex-direction: column;
        gap: 4px;
        .date_created {
          color: #b2b2b7;
        }
        .author {
          white-space: nowrap;
        }
      }
      &::before {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        transform: translate(2px, -21px);
        height: 0;
        width: 0;
        border: 12px solid transparent;
        border-top-color: #707080;
        z-index: 11;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.25s;
      }

      &:hover {
        .tooltip_content {
          opacity: 1;
        }
        &::before {
          opacity: 1;
        }
      }
    }
  }
  .right_section {
    flex: none;
    width: 22px;
    height: 22px;
    margin-left: auto;
    .action_btn {
      width: 22px;
      height: 22px;
      border: 2px solid #707080;
      background: #fff;
      display: flex;
      align-items: center;
      border-radius: 50%;
      justify-content: center;
      flex: none;
      cursor: pointer;
      .btn_icon {
        ::v-deep {
          .v-icon__svg {
            width: 14px;
            height: 14px;
            color: #707080;
            flex: none;
          }
        }
      }
      &:disabled {
        border-color: #d9d9d9;
        .btn_icon {
          ::v-deep {
            .v-icon__svg {
              color: #d9d9d9;
            }
          }
        }
      }
    }
    .custom_tooltip {
      position: relative;
      .tooltip_content {
        position: absolute;
        top: 0;
        right: 0;
        transform: translate(22px, calc(-100% - 20px));
        padding: 12px 16px;
        border-radius: 5px;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.25s;
        background-color: #ff5267;
        z-index: 10;
        width: 200px;
        font-size: 14px;
        line-height: 18px;
        color: #fff;
        text-align: center;
      }
      &::before {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        transform: translate(2px, -21px);
        height: 0;
        width: 0;
        border: 12px solid transparent;
        border-top-color: #ff5267;
        z-index: 11;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.25s;
      }

      &:hover {
        .tooltip_content {
          opacity: 1;
        }
        &::before {
          opacity: 1;
        }
      }
    }
    .action_progress {
      width: 22px;
      height: 22px;
      flex: none;
    }
  }
}
</style>
